import 'styles/earlybird/mobilepage.scss';

import cls from 'classnames';
import { selectActiveProduct } from 'core/redux/features/products/reducers/products';
import {
  NavTypes,
  actions as navActions,
} from 'core/redux/features/shared/nav';
import { AppState } from 'core/redux/reducers';
import { closest, height, listen, offset, scrollTop } from 'dom-helpers';
import qsa from 'dom-helpers/querySelectorAll';
import React, {
  createRef,
  RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavBurger } from '../../../Header/Nav';
import { useHandleBackButton, useSelectViewModes } from '../../hooks';
import { ViewGalleryOverlay } from './Gallery';

function handleScrollEvent(scrollableContainerEl, contentRef) {
  const offsetTop = scrollTop(scrollableContainerEl);
  // const offsetHeight = height(scrollableContainerEl)

  let contentOffset = null;
  if (contentRef.current) {
    contentOffset = offset(contentRef.current);
  }

  let pin = false;
  if (offsetTop >= 65) {
    pin = true;
  }

  let opacity;
  let colored = false;
  if (offsetTop >= 215) {
    colored = true;
  }

  if (pin) {
    opacity = offsetTop / (offsetTop - height(contentRef.current));
    if (offsetTop >= 65) {
      opacity += 0.5;
    }
  } else {
    opacity = 0.5;
  }

  // const calcTop = Math.round((offsetHeight ?? 0) - 60)
  return {
    offset: offsetTop,
    pin,
    opacity,
    colored,
    contentOffset,
    contentOffsetTop: offsetTop >= 216 ? 0 : 1000,
  };
}

type TProps = {
  activeViewModeId: string;
  showOnPin: boolean;
  mainRef: RefObject<HTMLDivElement>;
};

MobileTopHeader.defaultProps = {
  showOnPin: false,
  mainRef: createRef(),
};

export default function MobileTopHeader({
  mainRef,
  activeViewModeId,
  showOnPin: _,
}: TProps) {
  //
  // html refs
  //
  // const mainRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null);

  //
  // 3rd party hooks
  //
  // const history = useHistory()
  // const location = useLocation()
  const dispatch = useDispatch();

  //
  // selectors
  //
  const { viewMode: activeViewMode } = useSelectViewModes();
  const product = useSelector(selectActiveProduct);
  // const trans = useTrans()
  const { menuActive } = useSelector((state: AppState) => ({
    menuActive: state.nav.active,
  }));

  //
  // event handlers
  //

  // handle back button click
  const viewModeId = activeViewMode ? activeViewMode.id : null;
  const handleBackButton = useHandleBackButton(viewModeId);

  // handle click view modes nav menus
  // const handleViewModeChange = (e: React.SyntheticEvent, viewId: string) => {
  //   if (viewId === viewModeId) {
  //     e.preventDefault()
  //   }
  // }

  //
  // effects
  //

  const [{ pin, colored, contentOffsetTop }, setCalculatedStyles] = useState({
    offset: 0,
    opacity: 0.5,
    pin: false,
    colored: false,
    contentOffsetTop: -200,
  });

  // Auto show product name in header when scroll down
  // it listen to scroll to calculate if the name show show it the header
  // the calculation includes:
  // opacity - for overlay color effect
  // contentOffsetTop - position of product name
  // colored - the header should show blue color
  // pin - the header should position to fix top
  const scrollableContainerEl = useRef(null);
  const removeScrollEvent = useRef(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (removeScrollEvent.current) {
      removeScrollEvent.current();
    }

    if (!contentRef.current && mainRef.current) {
      const parent = closest(mainRef.current, '.pop-page-outer');
      if (!parent) {
        return;
      }

      const find = qsa(parent as HTMLElement, '.m-content');
      if (find.length > 0) {
        contentRef.current = find[0] as HTMLDivElement;
      }
    }

    if (mainRef.current && !scrollableContainerEl.current) {
      scrollableContainerEl.current = closest(
        mainRef.current,
        '.pop-page-outer',
      ) as HTMLElement;
    }

    if (scrollableContainerEl.current && !removeScrollEvent.current) {
      removeScrollEvent.current = listen(
        scrollableContainerEl.current,
        'scroll',
        () => {
          setCalculatedStyles(
            handleScrollEvent(scrollableContainerEl.current, contentRef),
          );
        },
      );
    }
    return () => {
      if (removeScrollEvent.current) {
        removeScrollEvent.current();
        removeScrollEvent.current = null;
      }
    };
  }, [mainRef, scrollableContainerEl, removeScrollEvent]);

  return (
    <div
      className={cls(
        'm-nav_contain',
        activeViewModeId === 'gallery' && 'hide',
        'm-nav_contain--pin',
        !pin && 'hide',
        colored && 'm-nav_contain--colored',
      )}
    >
      <div className="m-head__nav m-head__nav--left">
        <div onClick={handleBackButton}>
          <div className="eb-circle">
            <span className="eb eb-pl" />
          </div>
        </div>
      </div>
      <div
        className="n-head__name"
        style={{
          transform: `translate3d(0, ${contentOffsetTop}px, 0)`,
        }}
      >
        <div className="title">{product?.name}</div>
        {/*<p className="subtitle">{product?.short_description}</p>*/}
      </div>
      <div className="m-head__nav m-head__nav--right">
        <ViewGalleryOverlay product={product} />
        <NavBurger
          isActive={menuActive}
          onClick={_ => {
            dispatch(navActions.toggle(NavTypes.Menu));
          }}
        />
      </div>
    </div>
  );
}
